import { type SearchRequest } from '@searchkit/api';
import { merge } from 'merge-anything';

import { matchupCentreSourceFields } from '~/search-domain/pipelines/match-centre/query-config';
import { type SearchDomainContext, type SearchRuntimeSortingConfig } from '~/search-domain/types';
import { getSubeventBucketSize, SUBEVENT_TOTAL_AGGREGATION, toSortSelectionValue } from '~/search-domain/utils';

// Bound, not a reduction, given the supported-market filter runs first — see tests/query-factory.test.ts.
export const MARKET_BUCKET_CAP = 20;

// Capped per SIDE (`line.name`), never as one flat list — a flat cap can starve one team's lines
// entirely. See tests/query-factory.test.ts.
export const BET_SIDE_CAP = 4;

// Floor is 3, for three-way markets with no line (all sides share NO_LINE_BUCKET); this is double
// that because the response factory can still drop bets afterward on quality rules. See
// tests/query-factory.test.ts.
export const BETS_PER_SIDE_CAP = 6;

// Markets without a `line` (Win/Moneyline) share one bucket rather than being dropped.
export const NO_LINE_BUCKET = '__noline__';

export function createQueryFactory(config: {
  context: Pick<SearchDomainContext, 'isWalled'>;
  searchRequest: SearchRequest;
  sorting: SearchRuntimeSortingConfig;
}) {
  const { context, searchRequest, sorting } = config;

  const indexName = searchRequest.indexName;
  const sortOptions = sorting.options;
  const defaultSort = sortOptions[0];
  if (!defaultSort) {
    throw new Error('Default sort not found');
  }

  const activeSort =
    sortOptions.find(sort => `${indexName}${toSortSelectionValue(sort)}` === searchRequest.request.indexName) ??
    defaultSort;

  const hitsPerPage = searchRequest.request.params?.hitsPerPage ?? 10;
  const page = searchRequest.request.params?.page ?? 0;

  const order = { [activeSort.field]: activeSort.order };
  const sortAggregate = { [activeSort.field]: { max: { field: activeSort.field } } };

  // betId is nested one level under `line.name` so each side of a market is bounded separately.
  // `response-factory.ts` flattens it back in `toBetBuckets`; change the nesting here and that has
  // to change with it.
  const betAggregation = {
    terms: { field: 'line.name', missing: NO_LINE_BUCKET, size: BET_SIDE_CAP },
    aggs: {
      by_bet: {
        terms: { field: 'betId', size: BETS_PER_SIDE_CAP },
        aggs: {
          best_odds: {
            top_hits: {
              _source: { includes: matchupCentreSourceFields },
              size: 1,
              sort: { oddsDecimal: 'desc' as const },
            },
          },
        },
      },
    },
  };

  return merge(searchRequest, {
    body: {
      aggs: {
        stable_total: SUBEVENT_TOTAL_AGGREGATION,
        subevents: {
          terms: {
            field: 'subeventId',
            order,
            size: getSubeventBucketSize({ hitsPerPage, isWalled: context.isWalled, page }),
            // Sub-aggregations are only expanded for the surviving top-N subevents; the ordering
            // metric is a single-value agg so it is still computed in the first pass.
            collect_mode: 'breadth_first' as const,
          },
          aggs: {
            ...sortAggregate,
            markets: {
              terms: {
                field: 'marketId',
                size: MARKET_BUCKET_CAP,
              },
              aggs: {
                bets: betAggregation,
              },
            },
          },
        },
      },
      size: 0,
    },
  });
}
